import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  User,
  Search,
  Sparkles,
  DollarSign,
  TrendingUp,
  Shield,
  Settings,
  Zap,
  Brain,
  Rocket,
  Database,
  LayoutDashboard,
  ShoppingBag,
  Package,
  Trees,
  Box
} from 'lucide-react';

import GradientHero from '@/components/atoms/GradientHero';
import FeatureCard from '@/components/atoms/FeatureCard';
import CreatorPortfolio from '@/components/creator/CreatorPortfolio';
import MarketplaceSearch from '@/components/creator/MarketplaceSearch';
import AIRecommendations from '@/components/creator/AIRecommendations';
import RevenueAnalytics from '@/components/creator/RevenueAnalytics';
import DropshippingStore from '@/components/store/DropshippingStore';

const CreatorHub = () => {
  const [activeTab, setActiveTab] = useState('portfolio');
  const [marketplaceSubTab, setMarketplaceSubTab] = useState('search');
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0f0f15]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dark Mode Gradient Hero */}
        <GradientHero
          title="Turn ideas into products that sell"
          subtitle="AI-powered marketplace and creator hub. Manage your portfolio, discover tools, and track your success with intelligent automation."
          ctaPrimary="Explore Marketplace"
          ctaSecondary="View Templates"
          onPrimaryClick={() => setActiveTab('marketplace')}
          onSecondaryClick={() => navigate('/templates')}
        />

        {/* Quick Access Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <FeatureCard
            icon={Box}
            title="AIO Sandbox"
            description="All-in-one development environment"
            onClick={() => navigate('/aio-sandbox')}
            badge="NEW"
            gradient
          />
          <FeatureCard
            icon={User}
            title="Users"
            description="Manage platform users and permissions"
            onClick={() => navigate('/users')}
            gradient
          />
          <FeatureCard
            icon={ShoppingBag}
            title="Vendors"
            description="Browse verified marketplace vendors"
            onClick={() => navigate('/vendors')}
            gradient
          />
          <FeatureCard
            icon={Zap}
            title="Live Auctions"
            description="Bid on exclusive items and deals"
            onClick={() => navigate('/auctions')}
            badge="4 Active"
            gradient
          />
          <FeatureCard
            icon={Sparkles}
            title="AI Tools Hub"
            description="Discover 268+ AI tools across 27 categories"
            onClick={() => navigate('/ai-tools')}
            badge="268 Tools"
            gradient
          />
          <FeatureCard
            icon={Trees}
            title="TerraForge Studio"
            description="AI-powered landscaping with 1000+ plants"
            onClick={() => navigate('/terraforge-studio')}
            badge="1025 Plants"
            gradient
          />
          <FeatureCard
            icon={LayoutDashboard}
            title="Admin Dashboard"
            description="Full platform control and monitoring"
            onClick={() => navigate('/admin')}
            gradient
          />
        </div>

        {/* Modern Tabs Navigation - Dark Mode */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="bg-[#1a1a22] rounded-2xl border border-purple-500/20 p-2 shadow-lg">
            <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid bg-transparent gap-2">
              <TabsTrigger 
                value="portfolio" 
                className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white transition-all duration-200"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">Portfolio</span>
              </TabsTrigger>
              <TabsTrigger 
                value="marketplace" 
                className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white transition-all duration-200"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">Marketplace</span>
              </TabsTrigger>
              <TabsTrigger 
                value="recommendations" 
                className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white transition-all duration-200"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">AI Insights</span>
              </TabsTrigger>
              <TabsTrigger 
                value="revenue" 
                className="flex items-center gap-2 rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white transition-all duration-200"
              >
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">Analytics</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            <CreatorPortfolio />
          </TabsContent>

          {/* Marketplace Tab */}
          <TabsContent value="marketplace" className="space-y-6">
            {/* Marketplace Sub-Tabs */}
            <Tabs value={marketplaceSubTab} onValueChange={setMarketplaceSubTab}>
              <div className="bg-[#1a1a22] rounded-xl border border-purple-500/20 p-2 mb-6">
                <TabsList className="grid w-full grid-cols-2 bg-transparent gap-2">
                  <TabsTrigger
                    value="search"
                    className="flex items-center gap-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white"
                  >
                    <Search className="w-4 h-4" />
                    <span className="font-medium">Browse Tools</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="store"
                    className="flex items-center gap-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8d4dff] data-[state=active]:to-[#6a2ff0] data-[state=active]:text-white text-gray-400 hover:text-white"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    <span className="font-medium">Store</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="search">
                <MarketplaceSearch />
              </TabsContent>

              <TabsContent value="store">
                <DropshippingStore />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-6">
            <AIRecommendations />
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <RevenueAnalytics />
          </TabsContent>
        </Tabs>

        {/* Quick Stats Card (Visible on all tabs) */}
        <Card className="mt-8 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6" />
              <h3 className="text-xl font-bold">Creator Platform Stats</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-blue-100 text-sm mb-1">Portfolio Items</p>
                <p className="text-3xl font-bold">--</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Tools Discovered</p>
                <p className="text-3xl font-bold">5</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Total Revenue</p>
                <p className="text-3xl font-bold">$--</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreatorHub;
